import React, { useEffect, useState } from "react";
import {
  Card, Button, Table, Switch, Modal, Input, Select,
  notification, Tooltip, Typography, Tag, Upload, Spin,
} from "antd";
import { EditOutlined, ReloadOutlined, UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import { baseUrl } from "../../../config";

const { Text } = Typography;
const { Option } = Select;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
});

// Fields available in member context for template params
const PARAM_FIELDS = [
  { value: "name",              label: "Name" },
  { value: "father_name",       label: "Father Name" },
  { value: "firm_name",         label: "Firm / Shop Name" },
  { value: "current_city",      label: "Current City" },
  { value: "village",           label: "Village" },
  { value: "district",          label: "District" },
  { value: "tehsil",            label: "Tehsil" },
  { value: "phone_no",          label: "Phone" },
  { value: "date",              label: "Date" },
  { value: "message",           label: "Message" },
  { value: "birth_date",        label: "Birth Date" },
  { value: "birth_day",         label: "Day (1–31)" },
  { value: "birth_month",       label: "Month (1–12)" },
  { value: "birth_year",        label: "Year" },
  { value: "amount",            label: "Amount" },
  { value: "address",           label: "Address" },
];

export default function WhatsappConfigManager() {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [editRow, setEditRow]   = useState(null);   // row being edited
  const [saving, setSaving]     = useState(false);

  // modal form state
  const [formCampaign, setFormCampaign]   = useState("");
  const [formParams, setFormParams]       = useState([]);
  const [formMediaUrl, setFormMediaUrl]   = useState("");
  const [formActive, setFormActive]       = useState(true);
  const [uploading, setUploading]         = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/whatsapp-config/list`, { headers: authHeaders() });
      if (res.data.status) setRows(res.data.result);
    } catch (e) {
      notification.error({ message: "Failed to load WhatsApp config" });
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openEdit = (row) => {
    setEditRow(row);
    setFormCampaign(row.campaign_name || "");
    let tp = [];
    try { tp = JSON.parse(row.template_params || '["name"]'); } catch (_) { tp = ["name"]; }
    setFormParams(tp);
    setFormMediaUrl(row.media_url || "");
    setFormActive(!!row.is_active);
  };

  const handleSave = async () => {
    if (!formCampaign.trim()) {
      notification.warning({ message: "Campaign name is required" });
      return;
    }
    setSaving(true);
    try {
      await axios.patch(
        `${baseUrl}/whatsapp-config/${editRow.event_key}`,
        {
          campaign_name:   formCampaign.trim(),
          template_params: formParams,
          media_url:       formMediaUrl.trim() || null,
          is_active:       formActive,
        },
        { headers: authHeaders() }
      );
      notification.success({ message: "Config updated successfully" });
      setEditRow(null);
      load();
    } catch (e) {
      notification.error({ message: "Failed to save config" });
    }
    setSaving(false);
  };

  const handleMediaUpload = async ({ file }) => {
    const formData = new FormData();
    formData.append("media_image", file);
    setUploading(true);
    try {
      const res = await axios.post(`${baseUrl}/whatsapp-config/upload-media`, formData, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      if (res.data.status) {
        setFormMediaUrl(res.data.url);
        notification.success({ message: "Image uploaded successfully" });
      } else {
        notification.error({ message: res.data.message || "Upload failed" });
      }
    } catch (e) {
      notification.error({ message: "Upload failed" });
    }
    setUploading(false);
    return false; // prevent default upload behavior
  };

  const handleToggle = async (row, checked) => {
    try {
      await axios.patch(
        `${baseUrl}/whatsapp-config/${row.event_key}`,
        { is_active: checked },
        { headers: authHeaders() }
      );
      load();
    } catch (e) {
      notification.error({ message: "Failed to update status" });
    }
  };

  const columns = [
    {
      title: "Event",
      dataIndex: "event_name",
      key: "event_name",
      width: 200,
    },
    {
      title: "Event Key",
      dataIndex: "event_key",
      key: "event_key",
      width: 160,
      render: (v) => <Text code>{v}</Text>,
    },
    {
      title: "Campaign Name",
      dataIndex: "campaign_name",
      key: "campaign_name",
    },
    {
      title: "Template Params",
      dataIndex: "template_params",
      key: "template_params",
      render: (v) => {
        let fields = [];
        try { fields = JSON.parse(v || '["name"]'); } catch (_) { fields = ["name"]; }
        return (
          <span>
            {fields.map((f, i) => (
              <Tag key={i} color="blue">{`{{${i + 1}}} ${f}`}</Tag>
            ))}
          </span>
        );
      },
    },
    {
      title: "Media URL",
      dataIndex: "media_url",
      key: "media_url",
      render: (v) => v
        ? <Tooltip title={v}><Text style={{ maxWidth: 120, display: "inline-block" }} ellipsis>{v}</Text></Tooltip>
        : <Text type="secondary">None</Text>,
    },
    {
      title: "Active",
      dataIndex: "is_active",
      key: "is_active",
      width: 80,
      render: (v, row) => (
        <Switch checked={!!v} onChange={(checked) => handleToggle(row, checked)} />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, row) => (
        <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(row)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Card
      title="WhatsApp Event Config"
      extra={
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading} size="small">
          Refresh
        </Button>
      }
      style={{ marginBottom: 24 }}
    >
      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        Controls campaign name, template params (&#123;&#123;1&#125;&#125;, &#123;&#123;2&#125;&#125;…), and media URL for every
        event-triggered WhatsApp send (registration, purnima, etc.).
      </Text>

      <Table
        rowKey="id"
        dataSource={rows}
        columns={columns}
        loading={loading}
        pagination={false}
        size="small"
        scroll={{ x: true }}
      />

      <Modal
        open={!!editRow}
        title={`Edit — ${editRow?.event_name || ""}`}
        onCancel={() => setEditRow(null)}
        onOk={handleSave}
        confirmLoading={saving}
        width={560}
        okText="Save"
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>Campaign Name</Text>
          <Text type="secondary" style={{ display: "block", marginBottom: 4, fontSize: 12 }}>
            Must match the template name in AiSensy exactly.
          </Text>
          <Input
            value={formCampaign}
            onChange={(e) => setFormCampaign(e.target.value)}
            placeholder="e.g. New Registration"
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <Text strong>Template Params</Text>
          <Text type="secondary" style={{ display: "block", marginBottom: 4, fontSize: 12 }}>
            Select fields in order — first = &#123;&#123;1&#125;&#125;, second = &#123;&#123;2&#125;&#125;, etc.
          </Text>
          <Select
            mode="multiple"
            style={{ width: "100%" }}
            value={formParams}
            onChange={setFormParams}
            placeholder="Select fields in order"
          >
            {PARAM_FIELDS.map((f) => (
              <Option key={f.value} value={f.value}>{f.label}</Option>
            ))}
          </Select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Text strong>Media Image</Text>
          <Text type="secondary" style={{ display: "block", marginBottom: 4, fontSize: 12 }}>
            Upload an image to your server, or paste an existing URL. Leave blank if template has no media.
          </Text>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Input
              value={formMediaUrl}
              onChange={(e) => setFormMediaUrl(e.target.value)}
              placeholder="https://your-server.com/uploads/whatsapp_media/image.jpg"
              style={{ flex: 1 }}
            />
            <Upload
              accept="image/*"
              showUploadList={false}
              customRequest={handleMediaUpload}
            >
              <Button icon={uploading ? <Spin size="small" /> : <UploadOutlined />} disabled={uploading}>
                {uploading ? "Uploading…" : "Upload"}
              </Button>
            </Upload>
          </div>
          {formMediaUrl && (
            <img
              src={formMediaUrl}
              alt="preview"
              style={{ marginTop: 8, maxHeight: 80, maxWidth: "100%", borderRadius: 4, border: "1px solid #d9d9d9" }}
              onError={(e) => { e.target.style.display = "none"; }}
            />
          )}
        </div>

        <div>
          <Text strong>Active</Text>
          <div style={{ marginTop: 4 }}>
            <Switch checked={formActive} onChange={setFormActive} />
            <Text style={{ marginLeft: 8 }}>{formActive ? "Enabled" : "Disabled"}</Text>
          </div>
        </div>
      </Modal>
    </Card>
  );
}
